/* eslint-disable */
/// <reference lib="es2021.weakref" />
import { ApplicationRef, VERSION } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { DealerServiceToken } from '../factories/dealer.factory';
import { AppPhase } from '../models/communication/app-enums.model';
import { AppEventType } from '../models/communication/app-events.model';
import { WsClientService } from '../services/ws-client.service';

// [health] Temporary soak-test diagnostics, ported from IMAXAWebGames CoreMultiplay/Sources/Utils/HealthLog.ts: a "[health]" message on a
// schedule and when the page unloads; in the console healthReport() prints the trends, healthReport(true) the run before a reload and
// healthLog() writes a message now. Exact heap and alive counts need Chromium started with --enable-precise-memory-info
// --js-flags=--expose-gc (apps/consoleApp.js does that).
const FAST_MS = 2 * 60 * 1000; // for the first hour, to check that it works
const SLOW_MS = 30 * 60 * 1000; // after that, so a 48h run stays readable
const FAST_FOR_MS = 60 * 60 * 1000;
const STORE_KEY = `health:${location.pathname}`;
const PREVIOUS_KEY = `${STORE_KEY}:previous`;
const HANDLE_TAGS = ['video', 'audio', 'canvas', 'img', 'iframe'];
const ZONE_LISTENERS = /^__zone_symbol__(\w+)(true|false)$/; // zone.js keeps the listeners of every target in these properties
const PRESSURE = ['nominal', 'fair', 'serious', 'critical'];

type Sample = { h: number; why: string; values: Record<string, number> };

const round = (value: number) => Math.round((value || 0) * 100) / 100;
const fmt = (value: number) => (Math.abs(value) >= 100 ? Math.round(value) : round(value));
const bump = (map: Record<string, number>, key: string, by = 1) => (map[key] = (map[key] ?? 0) + by);
const clear = (...maps: Record<string, number>[]) => maps.forEach((map) => Object.keys(map).forEach((key) => delete map[key]));
const kilobytes = (data: any) => (data?.length ?? data?.byteLength ?? data?.size ?? 0) / 1024;
const isObject = (value: any): value is object => value !== null && typeof value === 'object';
const describe = (args: any[]) => {
    try {
        return args
            .map((arg) => arg?.message ?? String(arg))
            .join(' ')
            .replace(/\s+/g, ' ')
            .slice(0, 160);
    } catch {
        return '?';
    }
};

// Weak refs to every element made by script (everything Angular renders) and to the browser objects that hold OS handles or shared
// memory, with their kind: created counts them as they are made, the page scan counts the ones still alive
const created: Record<string, number> = {};
const refs = new Map<WeakRef<object>, string>();
function track<T extends object>(kind: string, obj: T): T {
    if (kind) bump(created, kind);
    refs.set(new WeakRef(obj), kind);
    return obj;
}

// Counts since the last message (reset after each one) and gauges read when it is written
const rates: Record<string, number> = {};
const requests: Record<string, number> = {};
const maxima: Record<string, number> = {};
const scriptMs: Record<string, number> = {};
const events: Record<string, number> = {}; // server events per name:phase:subPhase:code, and the requests the app sent
const timeouts = new Set<number>();
const intervals = new Set<number>();
const objectURLs = new Set<string>();
let socketsOpen = 0;
let xhrInFlight = 0;
let fetchInFlight = 0;
let rxOpen = 0;
let cpuPressure = -1;
let lagMs = 0;
let lagSamples = 0;
let requestMs = 0;
let requestCount = 0;
let lastError = '';
let lastWarn = '';

// The originals, for the health log's own work
const createElementNative = document.createElement.bind(document);
const createElementNSNative: any = document.createElementNS.bind(document);
const getContextNative: any = HTMLCanvasElement.prototype.getContext;
const setTimeoutNative = window.setTimeout.bind(window);
const setIntervalNative = window.setInterval.bind(window);
const clearTimeoutNative = window.clearTimeout.bind(window);
const clearIntervalNative = window.clearInterval.bind(window);
const requestAnimationFrameNative = window.requestAnimationFrame.bind(window);
const errorNative = console.error.bind(console);
const warnNative = console.warn.bind(console);

// DOM churn: nodes added and removed, high when a list re-renders all its items on every update
new MutationObserver((records) =>
    records.forEach((record) => {
        bump(rates, 'domAdded', record.addedNodes.length);
        bump(rates, 'domRemoved', record.removedNodes.length);
    })
).observe(document, { childList: true, subtree: true });

// Installed at import time, before Angular or the video player create anything
const noteElement = (element: Element) => track(HANDLE_TAGS.includes(element.localName) ? element.localName : '', element);
(document as any).createElement = (tag: string, options?: ElementCreationOptions) => noteElement(createElementNative(tag, options));
(document as any).createElementNS = (namespace: string | null, tag: string, options?: any) =>
    noteElement(createElementNSNative(namespace, tag, options));
const watchers: Record<string, (obj: any) => void> = {
    WebSocket: (socket: WebSocket) => {
        let opened = false;
        socket.addEventListener('open', () => {
            opened = true;
            socketsOpen++;
            bump(rates, 'wsOpens');
        });
        socket.addEventListener('close', () => {
            if (opened) socketsOpen--;
            bump(rates, 'wsCloses');
        });
        socket.addEventListener('message', (event) => {
            bump(rates, 'wsIn');
            bump(rates, 'wsInKB', kilobytes(event.data));
        });
    },
    XMLHttpRequest: (xhr: XMLHttpRequest) => {
        xhr.addEventListener('loadstart', () => xhrInFlight++);
        xhr.addEventListener('loadend', () => xhrInFlight--);
    }
};
const constructors: Record<string, string> = {
    Image: 'img',
    Audio: 'audio',
    AudioContext: 'AudioContext',
    WebSocket: 'WebSocket',
    RTCPeerConnection: 'RTCPeerConnection',
    Worker: 'Worker',
    XMLHttpRequest: 'XHR',
    MediaSource: 'MediaSource',
    EventSource: 'EventSource',
    BroadcastChannel: 'BroadcastChannel',
    ResizeObserver: 'ResizeObserver',
    IntersectionObserver: 'IntersectionObserver',
    MutationObserver: 'MutationObserver'
};
// zone.js runs a listener in the zone it was added in: ours go to the root zone, so they never trigger change detection
const rootZone = (window as any).Zone?.root;
const outsideAngular = (run: () => void) => (rootZone ? rootZone.run(run) : run());
for (const name in constructors) {
    const original = (window as any)[name];
    if (!original) continue;
    (window as any)[name] = new Proxy(original, {
        construct: (target, args, newTarget) => {
            const obj = Reflect.construct(target, args, newTarget);
            outsideAngular(() => watchers[name]?.(obj));
            return track(constructors[name], obj);
        }
    });
}
const send = WebSocket.prototype.send;
WebSocket.prototype.send = function (data) {
    bump(rates, 'wsOut');
    bump(rates, 'wsOutKB', kilobytes(data));
    send.call(this, data);
};
// Blob URLs keep their blob or MediaSource alive until revoked
const createObjectURL = URL.createObjectURL.bind(URL);
const revokeObjectURL = URL.revokeObjectURL.bind(URL);
URL.createObjectURL = (obj) => {
    const url = createObjectURL(obj);
    objectURLs.add(url);
    return url;
};
URL.revokeObjectURL = (url) => {
    objectURLs.delete(url);
    revokeObjectURL(url);
};

const play = HTMLMediaElement.prototype.play;
HTMLMediaElement.prototype.play = function () {
    bump(rates, 'mediaPlays');
    return play.call(this);
};
// Media Source Extensions: the stream player appends the video here, whatever it never removes stays in memory
if (window.SourceBuffer) {
    const appendBuffer = SourceBuffer.prototype.appendBuffer;
    SourceBuffer.prototype.appendBuffer = function (data) {
        bump(rates, 'mseAppendKB', kilobytes(data));
        appendBuffer.call(this, data);
    };
    const removeBuffer = SourceBuffer.prototype.remove;
    SourceBuffer.prototype.remove = function (start, end) {
        bump(rates, 'mseRemoves');
        removeBuffer.call(this, start, end);
    };
}
const contexts = new WeakSet<object>();
(HTMLCanvasElement.prototype as any).getContext = function (this: HTMLCanvasElement, type: string, ...args: any[]) {
    const context = getContextNative.call(this, type, ...args);
    if (context && !contexts.has(context)) {
        contexts.add(context);
        track(type === '2d' ? 'ctx2d' : type.startsWith('webgl') ? 'webgl' : type, context);
    }
    return context;
};
// Web Animations: Angular animations start one per element per transition
const animate = Element.prototype.animate;
Element.prototype.animate = function (...args: Parameters<Element['animate']>) {
    bump(rates, 'animateCalls');
    return animate.apply(this, args);
};

const fetchNative = window.fetch.bind(window);
window.fetch = (...args: Parameters<typeof fetch>) => {
    bump(rates, 'fetches');
    fetchInFlight++;
    return fetchNative(...args).finally(() => fetchInFlight--);
};
// Pending timeouts and live intervals: a count that keeps climbing means timers are scheduled faster than they finish
(window as any).setTimeout = (handler: any, ms?: number, ...args: any[]) => {
    bump(rates, 'timeoutsSet');
    if (typeof handler !== 'function') return setTimeoutNative(handler, ms, ...args);
    const id = setTimeoutNative(
        (...params: any[]) => {
            timeouts.delete(id);
            handler.apply(window, params);
        },
        ms,
        ...args
    );
    timeouts.add(id);
    return id;
};
(window as any).setInterval = (handler: any, ms?: number, ...args: any[]) => {
    bump(rates, 'intervalsSet');
    const id = setIntervalNative(handler, ms, ...args);
    intervals.add(id);
    return id;
};
// Each to its own original: zone.js books timeouts and intervals apart (ids are shared, so either set may hold it)
const forget = (id?: number) => (timeouts.delete(id!), intervals.delete(id!));
(window as any).clearTimeout = (id?: number) => (forget(id), clearTimeoutNative(id));
(window as any).clearInterval = (id?: number) => (forget(id), clearIntervalNative(id));
window.requestAnimationFrame = (callback) => (bump(rates, 'rafs'), requestAnimationFrameNative(callback));

document.addEventListener('visibilitychange', () => document.hidden && bump(rates, 'hidden'));
// Media events do not bubble, a capturing listener still sees them: playback that stopped for lack of data
document.addEventListener('waiting', () => bump(rates, 'videoWaiting'), true);
window.addEventListener(
    'error',
    (event) => {
        if (event.target !== window) bump(rates, 'resourceErrors'); // an image, video or script that failed to load
    },
    true
);
window.addEventListener('error', (event) => {
    bump(rates, 'errors');
    lastError = describe([event.message]);
});
window.addEventListener('unhandledrejection', (event) => {
    bump(rates, 'rejections');
    lastError = describe([event.reason]);
});
const addListener = EventTarget.prototype.addEventListener;
const removeListener = EventTarget.prototype.removeEventListener;
EventTarget.prototype.addEventListener = function (...args: Parameters<EventTarget['addEventListener']>) {
    bump(rates, 'domListenersAdded');
    addListener.apply(this, args);
};
EventTarget.prototype.removeEventListener = function (...args: Parameters<EventTarget['removeEventListener']>) {
    bump(rates, 'domListenersRemoved');
    removeListener.apply(this, args);
};
// Wrapped to count them, so DevTools shows this file as the source line of every console error and warning
console.error = (...args: any[]) => {
    bump(rates, 'consoleErrors');
    lastError = describe(args);
    errorNative(...args);
};
console.warn = (...args: any[]) => {
    bump(rates, 'consoleWarns');
    lastWarn = describe(args);
    warnNative(...args);
};

// Main thread time: long tasks, long animation frames (Chromium 123+) with the script entry points that took it, and slow taps
const observe = (type: string, handle: (entry: any) => void, options = {}) => {
    try {
        new PerformanceObserver((list) => list.getEntries().forEach(handle)).observe({ type, ...options });
    } catch {}
};
observe('resource', (entry) => bump(requests, entry.initiatorType));
observe('longtask', (entry) => {
    bump(rates, 'longTasks');
    bump(rates, 'longTaskMs', entry.duration);
    maxima.longTaskMsMax = Math.max(maxima.longTaskMsMax ?? 0, entry.duration);
});
observe('long-animation-frame', (entry) => {
    bump(rates, 'loafs');
    bump(rates, 'loafMs', entry.duration);
    bump(rates, 'loafBlockingMs', entry.blockingDuration);
    maxima.loafMsMax = Math.max(maxima.loafMsMax ?? 0, entry.duration);
    entry.scripts?.forEach((script: any) => {
        bump(rates, 'forcedLayoutMs', script.forcedStyleAndLayoutDuration);
        bump(scriptMs, script.invoker || script.invokerType, script.duration);
    });
});
observe(
    'event',
    (entry) => {
        maxima.inputMsMax = Math.max(maxima.inputMsMax ?? 0, entry.duration);
        if (entry.duration > 100) bump(rates, 'slowInputs');
    },
    { durationThreshold: 16 }
);
// CPU pressure of the whole machine (Chromium 125+): 0 nominal, 1 fair, 2 serious, 3 critical, -1 no reading (no API or window not focused)
try {
    new (window as any).PressureObserver((records: any[]) =>
        records.forEach((record) => {
            cpuPressure = PRESSURE.indexOf(record.state);
            maxima.cpuPressureMax = Math.max(maxima.cpuPressureMax ?? 0, cpuPressure);
        })
    )
        .observe('cpu', { sampleInterval: 1000 })
        .catch(() => {});
} catch {}
// Event loop lag: how late a 1 s timer fires, it climbs when the main thread is busy
const lagTimer = (start: number) =>
    setTimeoutNative(() => {
        const lag = Math.max(0, performance.now() - start - 1000);
        lagMs += lag;
        lagSamples++;
        maxima.lagMsMax = Math.max(maxima.lagMsMax ?? 0, lag);
        lagTimer(performance.now());
    }, 1000);
lagTimer(performance.now());

// Open RxJS subscriptions, not unsubscribed and not garbage collected: one that keeps climbing is held by a long-lived subject
const subscriptions = new WeakMap<object, { open: boolean }>();
const collected = new FinalizationRegistry<{ open: boolean }>((state) => state.open && rxOpen--);
const subscribe = Observable.prototype.subscribe as any;
(Observable.prototype as any).subscribe = function (...args: any[]) {
    const subscription: Subscription = subscribe.apply(this, args);
    bump(rates, 'rxSubscribes');
    if (!subscription.closed && !subscriptions.has(subscription)) {
        const state = { open: true };
        subscriptions.set(subscription, state);
        collected.register(subscription, state);
        rxOpen++;
    }
    return subscription;
};
const unsubscribe = Subscription.prototype.unsubscribe;
Subscription.prototype.unsubscribe = function () {
    const state = subscriptions.get(this);
    if (state?.open) {
        state.open = false;
        rxOpen--;
    }
    unsubscribe.call(this);
};
// Angular change detection: how often it runs and how long it takes (_tick is what tick() and the zone scheduler call)
const appRefPrototype = ApplicationRef.prototype as any;
const tick = appRefPrototype._tick;
if (tick)
    appRefPrototype._tick = function (...args: any[]) {
        const start = performance.now();
        try {
            return tick.apply(this, args);
        } finally {
            const ms = performance.now() - start;
            bump(rates, 'cdTicks');
            bump(rates, 'cdMs', ms);
            maxima.cdMsMax = Math.max(maxima.cdMsMax ?? 0, ms);
        }
    };
// Requests the app sends (dealer and supervisor actions, history...) per name:action, the ones that failed, and their round trip
const sendRequest = WsClientService.prototype.sendRequestMessage as any;
(WsClientService.prototype as any).sendRequestMessage = async function (request: string, data?: any) {
    const name = [request, data?.action].filter(Boolean).join(':');
    const start = performance.now();
    bump(events, `request:${name}`);
    try {
        return await sendRequest.call(this, request, data);
    } catch (error) {
        bump(events, `requestFailed:${name}`);
        throw error;
    } finally {
        const ms = performance.now() - start;
        requestMs += ms;
        requestCount++;
        maxima.requestMsMax = Math.max(maxima.requestMsMax ?? 0, ms);
    }
};

// Samples live only in localStorage, so the log's own memory stays flat and after a crash or a reload healthReport(true) still
// shows the run before it
const save = (run: Sample[], messages: string[]) => {
    try {
        const keys = [...new Set(run.flatMap((sample) => Object.keys(sample.values)))];
        const rows = run.map((sample) => [sample.h, sample.why, ...keys.map((key) => sample.values[key] ?? null)]);
        localStorage.setItem(STORE_KEY, JSON.stringify({ keys, rows, messages }));
    } catch {}
};
const load = (key: string): { run: Sample[]; messages: string[] } => {
    try {
        const { keys, rows, messages } = JSON.parse(localStorage.getItem(key) ?? '');
        const run = rows.map(([h, why, ...values]: any[]) => ({
            h,
            why,
            values: Object.fromEntries(keys.map((name: string, i: number) => [name, values[i]]).filter((entry: any[]) => entry[1] != null))
        }));
        return { run, messages };
    } catch {
        return { run: [], messages: [] };
    }
};
// Every page load starts a new run; the one before it is kept if it lasted over half an hour
const stored = load(STORE_KEY).run;
try {
    if ((stored[stored.length - 1]?.h ?? 0) > 0.5) localStorage.setItem(PREVIOUS_KEY, localStorage.getItem(STORE_KEY)!);
    localStorage.removeItem(STORE_KEY);
} catch {}

// Every metric that changed, at a few points in time, sorted so the ones that climbed the most come first
(window as any).healthReport = (previous = false): string => {
    const { run, messages } = load(previous ? PREVIOUS_KEY : STORE_KEY);
    if (run.length < 2) return 'Not enough samples yet';
    if (previous) messages.forEach((message) => warnNative(message));
    const last = run[run.length - 1];
    const near = (h: number) => run.reduce((best, sample) => (Math.abs(sample.h - h) < Math.abs(best.h - h) ? sample : best));
    const incidents = run.filter((sample) => sample.why !== 'periodic').slice(0, 4);
    const hours = [1, 2, 4, 8, 12, 16, 20, 24, 36, 48].filter((h) => h < last.h).map(near);
    const marks = [...new Set([run[0], ...hours, ...incidents, last])].sort((a, b) => a.h - b.h);
    const base = near(1);
    const value = (sample: Sample, key: string) => sample.values[key] ?? 0;
    const growth = (key: string) => (value(last, key) - value(base, key)) / Math.max(1, Math.abs(value(base, key)));
    const keys = [...new Set(run.flatMap((sample) => Object.keys(sample.values)))];
    const changed = keys
        .filter((key) => marks.some((sample) => value(sample, key) !== value(base, key)))
        .sort((a, b) => growth(b) - growth(a));
    const label = (sample: Sample) => (sample.why === 'periodic' ? `${sample.h.toFixed(1)}h` : `${sample.h.toFixed(2)}h ${sample.why}`);
    console.table(
        Object.fromEntries(changed.map((key) => [key, Object.fromEntries(marks.map((sample) => [label(sample), fmt(value(sample, key))]))]))
    );
    return `${run.length} samples over ${last.h.toFixed(2)}h: ${changed.length} of ${keys.length} metrics changed, sorted by growth since ${base.h.toFixed(1)}h`;
};

export function startHealthLog(appRef: ApplicationRef): void {
    // A round is a phase change with a new roundId (NewRoundPreparation, BetsOpen; roulette repeats BetsOpen for every sub-phase),
    // and every game ends one with RoundEnd, RoundVoid or RoundError, each counted once per round
    const dealer = appRef.injector.get(DealerServiceToken);
    const outcomes: Record<string, number> = { [AppPhase.RoundEnd]: 0, [AppPhase.RoundVoid]: 0, [AppPhase.RoundError]: 0 };
    const countedFor = new Map<string, unknown>(); // outcome -> the round it was last counted for
    let rounds = 0;
    let roundId: unknown;
    dealer.getEventsObservable().subscribe(({ name, data }) => {
        const { phase, subPhase, code, errorCode, roundId: id } = (data ?? {}) as any;
        bump(events, [name, phase, subPhase, code, errorCode].filter((part) => part !== undefined).join(':'));
        if (name !== AppEventType.GamePhaseChange) return;
        if (id !== undefined && id !== roundId) {
            roundId = id;
            rounds++;
        }
        if (phase in outcomes && !(countedFor.has(phase) && countedFor.get(phase) === roundId)) {
            countedFor.set(phase, roundId);
            outcomes[phase]++;
        }
    });

    // One pass over the page: elements in it, elements made by script that are alive but detached, handles, listeners per owner:event
    const qualities = new WeakMap<HTMLVideoElement, VideoPlaybackQuality>();
    const since = (now: number, before = 0) => (now >= before ? now - before : now); // video counters restart with a new source
    const scanPage = () => {
        const app = { components: 0, detachedComponents: 0 };
        const browser = { dom: 0, domNodes: 0, detached: 0, listeners: 0, windowListeners: 0, documentListeners: 0, detachedListeners: 0 };
        const media = {
            videos: 0,
            mediaPlaying: 0,
            videoBufferedSec: 0,
            videoBehindSec: 0,
            sourceBuffers: 0,
            audioRunning: 0,
            animations: 0,
            animationsRunning: 0,
            canvasMB: 0,
            imgMB: 0
        };
        const alive: Record<string, number> = {};
        const listeners: Record<string, number> = {};
        const objects: Record<string, number> = {};
        const detached: Record<string, number> = {};
        const countListeners = (target: any, owner: string): number => {
            let total = 0;
            Object.keys(target).forEach((key) => {
                const match = ZONE_LISTENERS.exec(key);
                const count = (match && target[key]?.length) || 0;
                if (!count) return;
                total += count;
                bump(listeners, `${owner}:${match![1]}`, count);
            });
            browser.listeners += total;
            return total;
        };
        browser.windowListeners = countListeners(window, 'window');
        browser.documentListeners = countListeners(document, 'document');
        document.querySelectorAll('*').forEach((element) => {
            browser.dom++;
            bump(objects, element.localName);
            if (element.localName.startsWith('app-')) app.components++;
            countListeners(element, element.localName);
        });
        const walker = document.createTreeWalker(document);
        while (walker.nextNode()) browser.domNodes++;
        refs.forEach((kind, ref) => {
            const obj: any = ref.deref();
            if (!obj) {
                refs.delete(ref);
                return;
            }
            if (kind) bump(alive, kind);
            if (!(obj instanceof Element)) countListeners(obj, kind);
            else if (!obj.isConnected) {
                const owner = `${obj.localName}(detached)`;
                browser.detached++;
                if (obj.localName.startsWith('app-')) app.detachedComponents++;
                bump(objects, owner);
                bump(detached, obj.localName);
                browser.detachedListeners += countListeners(obj, owner);
            }
            if (obj instanceof HTMLMediaElement && !obj.paused && !obj.ended) media.mediaPlaying++;
            if (obj instanceof HTMLCanvasElement) media.canvasMB += (obj.width * obj.height * 4) / 1048576;
            if (obj instanceof HTMLImageElement) media.imgMB += (obj.naturalWidth * obj.naturalHeight * 4) / 1048576;
            if (obj instanceof MediaSource) media.sourceBuffers += obj.sourceBuffers.length;
            if (obj instanceof AudioContext && obj.state === 'running') media.audioRunning++;
            if (obj instanceof HTMLVideoElement) {
                media.videos++;
                const quality = obj.getVideoPlaybackQuality();
                const last = qualities.get(obj);
                bump(rates, 'videoFrames', since(quality.totalVideoFrames, last?.totalVideoFrames));
                bump(rates, 'videoDropped', since(quality.droppedVideoFrames, last?.droppedVideoFrames));
                qualities.set(obj, quality);
                const { buffered } = obj;
                if (buffered.length) {
                    const end = buffered.end(buffered.length - 1);
                    media.videoBufferedSec += end - buffered.start(0);
                    media.videoBehindSec = Math.max(media.videoBehindSec, end - obj.currentTime);
                }
            }
        });
        document.getAnimations().forEach((animation: any) => {
            media.animations++;
            if (animation.playState === 'running') media.animationsRunning++;
            bump(objects, `animation:${animation.animationName || animation.transitionProperty || 'script'}`);
        });
        return { app, browser, media, alive, listeners, objects, detached };
    };
    type Scan = ReturnType<typeof scanPage>;

    // Sizes of every array, Map and Set held by the root services and by the components on the page (ng.getComponent is Angular's
    // dev-mode debug API, so components only in dev builds); paths are Class.property, nested items add up under path[]
    const walkData = (): Record<string, number> => {
        const data: Record<string, number> = {};
        const seen = new Set<object>([appRef.injector]);
        const visit = (value: any, path: string, depth: number) => {
            if (seen.has(value) || value === window || value instanceof Node || ArrayBuffer.isView(value)) return;
            seen.add(value);
            try {
                const items = Array.isArray(value) || value instanceof Map || value instanceof Set ? value : null;
                if (items) bump(data, path, Array.isArray(items) ? items.length : items.size);
                if (depth === 0) return;
                const itemPath = `${path}[]`;
                if (items) items.forEach((item: any) => isObject(item) && visit(item, itemPath, depth - 1));
                else Object.keys(value).forEach((key) => isObject(value[key]) && visit(value[key], `${path}.${key}`, depth - 1));
            } catch {} // cross-origin windows and the like
        };
        const roots = new Map<object, string>();
        (appRef.injector as any).records?.forEach((record: any, token: any) => {
            const value = record?.value; // null for tokens looked up and not found
            if (isObject(value) && !seen.has(value))
                roots.set(value, typeof token === 'function' ? token.name : String(token).replace('InjectionToken ', ''));
        });
        document.querySelectorAll('*').forEach((element) => {
            const component = element.localName.startsWith('app-') && (window as any).ng?.getComponent(element);
            if (component) roots.set(component, component.constructor.name);
        });
        // Each root under its own name: the others stay in seen until their turn, so one service is not walked as part of another
        roots.forEach((_, root) => seen.add(root));
        roots.forEach((path, root) => {
            seen.delete(root);
            visit(root, path, 3);
        });
        return data;
    };
    // Taken at the first message after the warm-up hour; later messages list the biggest increases since then
    let baseline: Record<string, Record<string, number>> | null = null;
    const growth = (scan: Scan): string[] => {
        if (performance.now() < FAST_FOR_MS) return [];
        const now: Record<string, Record<string, number>> = { listeners: scan.listeners, objects: scan.objects, data: walkData() };
        if (!baseline) {
            baseline = now;
            return [' grew baseline taken, increases are listed from the next message on'];
        }
        const base = baseline;
        return Object.keys(now).map((kind) => {
            const top = Object.keys(now[kind])
                .map((key): [string, number] => [key, now[kind][key] - (base[kind][key] ?? 0)])
                .filter(([, delta]) => delta > 0)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 20) // one leak fills about 8 places (a countdown is 8 tags), so a second one stays visible
                .map(([key, delta]) => `${key}+${delta}=${now[kind][key]}`);
            return ` grew ${kind} ${top.join(' ') || '-'}`;
        });
    };

    let lastTime = performance.now();
    const collect = (scan: Scan): Record<string, number> => {
        const minutes = Math.max(0.01, (performance.now() - lastTime) / 60000);
        const perTenMinutes = (map: Record<string, number>) =>
            Object.fromEntries(Object.entries(map).map(([key, value]) => [key, (value * 10) / minutes]));
        const memory = (performance as any).memory ?? {};
        const kinds = Object.fromEntries(
            Object.keys(created).flatMap((kind) => [
                [kind, created[kind]],
                [`${kind}Alive`, scan.alive[kind] ?? 0]
            ])
        );
        const sections: Record<string, Record<string, number>> = {
            app: {
                rounds,
                roundEnds: outcomes[AppPhase.RoundEnd],
                roundVoids: outcomes[AppPhase.RoundVoid],
                roundErrors: outcomes[AppPhase.RoundError],
                taps: (performance as any).eventCounts?.get('pointerdown') ?? 0,
                requestMsAvg: requestMs / Math.max(1, requestCount),
                ...scan.app,
                rootViews: appRef.viewCount,
                rxSubscriptions: rxOpen
            },
            browser: {
                heapMB: (memory.usedJSHeapSize ?? 0) / 1048576,
                heapTotalMB: (memory.totalJSHeapSize ?? 0) / 1048576,
                ...scan.browser,
                ...kinds,
                timeouts: timeouts.size,
                intervals: intervals.size,
                objectURLs: objectURLs.size,
                socketsOpen,
                xhrInFlight,
                fetchInFlight,
                styleSheets: document.styleSheets.length,
                fontFaces: (document.fonts as any).size,
                perfEntries: performance.getEntries().length,
                historyLength: history.length
            },
            media: scan.media,
            cpu: { pressure: cpuPressure, lagMsAvg: lagMs / Math.max(1, lagSamples) },
            per10min: { ...perTenMinutes(rates), ...maxima, cpuPressureMax: Math.max(maxima.cpuPressureMax ?? 0, cpuPressure) },
            requests: perTenMinutes(requests),
            events: perTenMinutes(events),
            screen: {
                width: window.innerWidth,
                height: window.innerHeight,
                dpr: window.devicePixelRatio,
                visible: document.hidden ? 0 : 1,
                online: navigator.onLine ? 1 : 0
            }
        };
        return Object.fromEntries(
            Object.entries(sections).flatMap(([section, values]) =>
                Object.entries(values).map(([key, value]) => [`${section}.${key}`, round(value)])
            )
        );
    };

    const top = (map: Record<string, number>, count: number) =>
        Object.keys(map)
            .sort((a, b) => map[b] - map[a])
            .slice(0, count);
    const format = (why: string, h: number, values: Record<string, number>, scan: Scan): string => {
        const keysOf = (section: string) =>
            Object.keys(values)
                .filter((key) => key.startsWith(`${section}.`))
                .map((key) => key.slice(section.length + 1));
        const show = (section: string, keys = keysOf(section)) =>
            keys.length === 0
                ? ''
                : ` ${section} ` +
                  keys
                      .filter((key) => !(key.endsWith('Alive') && keys.includes(key.slice(0, -5))))
                      .map((key) => {
                          const pair = values[`${section}.${key}Alive`];
                          return `${key}=${fmt(values[`${section}.${key}`])}${pair === undefined ? '' : `/${fmt(pair)}`}`;
                      })
                      .join(' ');
        const busiest = keysOf('events').sort((a, b) => values[`events.${b}`] - values[`events.${a}`]);
        const scripts = top(scriptMs, 3).map((script) => `${script} ${Math.round(scriptMs[script])}ms`);
        const detached = top(scan.detached, Infinity).map((tag) => `${tag}=${scan.detached[tag]}`); // every tag
        const state = dealer.getState(); // undefined until the server's first message
        const context = state
            ? ` table=${state.configuration?.tableId} game=${state.configuration?.game?.displayName}` +
              ` phase=${state.roundState?.phase} round=${roundId}`
            : '';
        return [
            `[health] ${why} ${h.toFixed(2)}h ${new Date().toISOString()}${context}`,
            ...['app', 'browser', 'media', 'cpu', 'per10min', 'requests', 'screen'].map((section) => show(section)),
            show('events', busiest),
            ` topScripts ${scripts.join(' | ') || '-'}`,
            ` topDetached ${detached.join(' ') || '-'}`,
            ` last error="${lastError}" warn="${lastWarn}"`
        ]
            .filter(Boolean)
            .join('\n');
    };

    const log = (why: string) => {
        if (why !== 'unload') (window as any).gc?.(); // only with --js-flags=--expose-gc: alive counts and heap without uncollected garbage
        const h = performance.now() / 3600000;
        const scan = scanPage();
        const values = collect(scan);
        let grew: string[];
        try {
            grew = growth(scan);
        } catch (error) {
            grew = [` grew failed ${describe([error])}`];
        }
        const message = [format(why, h, values, scan), ...grew].join('\n');
        warnNative(message);
        const { run, messages } = load(STORE_KEY);
        run.push({ h, why, values });
        if (run.length > 300) run.splice(1, 1); // the first sample stays as the baseline
        save(run, [...messages, message].slice(-3));
        clear(rates, requests, maxima, events, scriptMs);
        lagMs = lagSamples = requestMs = requestCount = 0;
        lastTime = performance.now();
    };

    // The GPU Chromium renders with: SwiftShader or llvmpipe means software rendering on the CPU
    const probe = getContextNative.call(createElementNative('canvas'), 'webgl');
    const info = probe?.getExtension('WEBGL_debug_renderer_info');
    const gpu = probe ? probe.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : probe.RENDERER) : 'none';
    probe?.getExtension('WEBGL_lose_context')?.loseContext();
    warnNative(
        `[health] boot ${new Date().toISOString()} ${location.pathname} angular=${VERSION.full} gpu=${gpu} cores=${navigator.hardwareConcurrency}` +
            ` memoryGB=${(navigator as any).deviceMemory} heapLimitMB=${Math.round(((performance as any).memory?.jsHeapSizeLimit ?? 0) / 1048576)}` +
            ` gc=${(window as any).gc ? 'on' : 'off'} screen=${screen.width}x${screen.height} ${navigator.userAgent}` +
            ` - type healthReport() for the trends`
    );
    (window as any).healthLog = () => log('manual');
    const next = () =>
        setTimeoutNative(
            () => {
                try {
                    log('periodic');
                } finally {
                    next();
                }
            },
            performance.now() < FAST_FOR_MS ? FAST_MS : SLOW_MS
        );
    next();
    window.addEventListener('pagehide', () => log('unload'));
}
